import * as React from 'react';
import MailOutlinedIcon from '@material-ui/icons/MailOutlined';
import { fetchIncomingSubcontractRequestsCount } from '@pec/aion-ui-core/actions/fetchSubcontractsRequestsCount';
import { fetchPendingConnectionsCount } from 'actions/connections';
import { selectIsChasContractor } from '@pec/aion-ui-core/selectors/organization';
import { ToDoDescriptor } from '../types';
import { useAppDispatch, useTypedSelector } from '../../../../combineReducers';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type RouteParams = {
  organizationId: string;
};

export const useClientConnectionsToDo = (): ToDoDescriptor[] => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const { pendingConnectionsCount, isFetchingPendingConnectionsCount } = useTypedSelector(state => state.connections);
  const { incomingSubcontractRequests } = useTypedSelector(state => state.notifications);
  const isChas = useTypedSelector(selectIsChasContractor);

  // CPRO-24374: this item's visibility is driven by the real pending-connection counts (clients +
  // prime/subcontractors), not by profile completeness. Fetch the counts for any org that could show the
  // item (null = not yet fetched) so the decision is made from live data. This is also what fixes the gap
  // where a complete-profile org with pending connections used to render nothing.
  React.useEffect(() => {
    if (organizationId && pendingConnectionsCount === null) {
      dispatch(fetchPendingConnectionsCount(organizationId));
    }
  }, [organizationId, pendingConnectionsCount, dispatch]);

  React.useEffect(() => {
    if (organizationId && !isChas && incomingSubcontractRequests === null) {
      dispatch(fetchIncomingSubcontractRequestsCount(organizationId));
    }
  }, [organizationId, isChas, incomingSubcontractRequests, dispatch]);

  const clientCount = pendingConnectionsCount ?? 0;
  const scmCount = isChas ? 0 : incomingSubcontractRequests ?? 0;
  const scmLink = `/organizations/${organizationId}/subcontractor-management/incoming`;
  const clientLink = `/organizations/${organizationId}/my-connections`;

  // Both: group item with expandable children.
  if (clientCount > 0 && scmCount > 0) {
    return [
      {
        id: 'myConnections',
        variant: 'group',
        // ToDoGroupItem appends "({count})" — label must not include the count itself.
        label: t('newDashboard.toDoWidget.reviewPendings', 'Review pending connections'),
        icon: <MailOutlinedIcon />,
        count: clientCount + scmCount,
        children: [
          {
            id: 'fromClients',
            label: t('newDashboard.toDoWidget.fromClients', 'From clients ({{count}})', { count: clientCount }),
            href: clientLink
          },
          {
            id: 'fromPrimeSub',
            label: t('newDashboard.toDoWidget.fromPrimeSub', 'From prime/subcontractors ({{count}})', {
              count: scmCount
            }),
            href: scmLink
          }
        ],
        isVisible: true,
        isFetching: isFetchingPendingConnectionsCount
      }
    ];
  }

  // SCM-only: simple link to the SCM incoming page (isChas already forces scmCount to 0 above).
  if (scmCount > 0) {
    return [
      {
        id: 'myConnections',
        variant: 'link',
        label: t(
          'newDashboard.toDoWidget.reviewPendingPrimeSubConnections',
          'Review pending prime/sub connections ({{count}})',
          { count: scmCount }
        ),
        icon: <MailOutlinedIcon />,
        href: scmLink,
        isVisible: true,
        // No isFetchingIncomingSubcontractRequests flag in the store; spinner unavailable for this item.
        isFetching: false
      }
    ];
  }

  // Client-only. The count is interpolated into the label by i18n; `count` must stay undefined on this
  // descriptor or ToDoLinkItem would append a second "(N)".
  return [
    {
      id: 'myConnections',
      variant: 'link',
      label: t(
        'newDashboard.toDoWidget.reviewPendingClientConnections',
        'Review pending client connections ({{count}})',
        {
          count: clientCount
        }
      ),
      icon: <MailOutlinedIcon />,
      href: clientLink,
      // CPRO-24374: never render when there are zero pending connections (would otherwise show "(0)").
      isVisible: clientCount > 0,
      // Only hold the widget spinner open for a fetch this hook actually issued.
      isFetching: isFetchingPendingConnectionsCount
    }
  ];
};
